import { ResponsiveTable } from '../components/ResponsiveTable';
import { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { 
  Search, 
  Filter, 
  ChevronDown, 
  ChevronLeft, 
  ChevronRight, 
  Edit, 
  Plus,
  MapPin,
  AlertCircle,
  Loader2,
  Trash2
} from 'lucide-react';
import { HelpIcon } from '../components/HelpIcon';
import { TableScrollContainer } from '../components/TableScrollContainer';
import { useVisibleSelection } from '../hooks/useVisibleSelection';
import { ConfirmDefinitiveDeleteModal } from '../components/ConfirmDefinitiveDeleteModal';
import { RootState } from '../store';
import { listItemCollections, type ItemCollection } from '../services/itemCollectionsApi';

interface BodyZone {
  id: string;
  name: string;
  category: string;
  description?: string;
  itemCollectionId?: string;
  itemCollectionName?: string;
  createdAt: string;
  updatedAt: string;
}

interface BodyZonesResponse {
  success: boolean;
  data: {
    bodyZones: BodyZone[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  message?: string;
}

export function BodyZonesListPage() {
  const { user } = useSelector((state: RootState) => state.auth);
  const canHardDelete = user?.role === 'super_admin';
  
  const [bodyZones, setBodyZones] = useState<BodyZone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingBodyZone, setEditingBodyZone] = useState<BodyZone | null>(null);
  
  // Filters and pagination
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [collectionFilter, setCollectionFilter] = useState('');
  const [collections, setCollections] = useState<ItemCollection[]>([]);
  const [sortKey, setSortKey] = useState('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  const limit = 20;

  const visibleBodyZoneIds = useMemo(() => bodyZones.map((z) => z.id), [bodyZones]);
  const {
    selectedIds: selectedBodyZoneIds,
    selectedCount,
    clearSelection,
    selectAllCheckboxRef,
    allVisibleSelected,
    toggleSelect,
    toggleSelectAllVisible,
  } = useVisibleSelection<string>(visibleBodyZoneIds);

  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState<{ isOpen: boolean; ids: string[] }>({
    isOpen: false,
    ids: [],
  });
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkHardDeleteConfirm, setBulkHardDeleteConfirm] = useState<{ isOpen: boolean; ids: string[] }>({
    isOpen: false,
    ids: [],
  });
  const [bulkHardDeleting, setBulkHardDeleting] = useState(false);

  useEffect(() => {
    listItemCollections({ limit: 1000, sortKey: 'name', sortDir: 'asc' })
      .then((data) => setCollections(data.itemCollections || []))
      .catch(() => setCollections([]));
  }, []);

  useEffect(() => {
    fetchBodyZones();
  }, [search, categoryFilter, collectionFilter, sortKey, sortDir, page]);

  const fetchBodyZones = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortKey,
        sortDir,
        ...(search && { search }),
        ...(categoryFilter && { category: categoryFilter }),
        ...(collectionFilter && { itemCollectionId: collectionFilter })
      });

      const response = await fetch(`/api/admin/body-zones?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch body zones');
      }

      const data: BodyZonesResponse = await response.json();
      
      if (data.success) {
        setBodyZones(data.data.bodyZones);
        setTotal(data.data.total);
        setTotalPages(data.data.totalPages);
      } else {
        setError(data.message || 'Failed to load body zones');
      }
    } catch (error) {
      console.error('Fetch body zones error:', error);
      setError('Failed to load body zones');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const handleCreate = () => {
    setEditingBodyZone(null);
    setShowCreateModal(true);
  };

  const handleEdit = (bodyZone: BodyZone) => {
    setEditingBodyZone(bodyZone);
    setShowCreateModal(true);
  };

  const handleDelete = async (bodyZoneId: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette zone du corps ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/admin/body-zones/${bodyZoneId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchBodyZones();
      } else {
        setError('Failed to delete body zone');
      }
    } catch (error) {
      console.error('Delete body zone error:', error);
      setError('Failed to delete body zone');
    }
  };

  const openBulkDeleteConfirm = () => {
    if (selectedBodyZoneIds.size === 0) return;
    setBulkDeleteConfirm({ isOpen: true, ids: Array.from(selectedBodyZoneIds) });
  };
  const openBulkHardDeleteConfirm = () => {
    if (selectedBodyZoneIds.size === 0) return;
    setBulkHardDeleteConfirm({ isOpen: true, ids: Array.from(selectedBodyZoneIds) });
  };
  const cancelBulkDelete = () => setBulkDeleteConfirm({ isOpen: false, ids: [] });
  const confirmBulkDelete = async () => {
    if (bulkDeleteConfirm.ids.length === 0) return;
    try {
      setBulkDeleting(true);
      setError(null);
      const token = localStorage.getItem('token');
      const failed: string[] = [];

      for (const id of bulkDeleteConfirm.ids) {
        try {
          const resp = await fetch(`/api/admin/body-zones/${id}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!resp.ok) failed.push(id);
        } catch {
          failed.push(id);
        }
      }

      setBulkDeleteConfirm({ isOpen: false, ids: [] });
      clearSelection();
      await fetchBodyZones();

      if (failed.length > 0) {
        setError(`${failed.length} zone(s) n’ont pas pu être supprimées.`);
      }
    } finally {
      setBulkDeleting(false);
    }
  };
  const cancelBulkHardDelete = () => setBulkHardDeleteConfirm({ isOpen: false, ids: [] });
  const confirmBulkHardDelete = async () => {
    if (bulkHardDeleteConfirm.ids.length === 0) return;
    try {
      setBulkHardDeleting(true);
      setError(null);
      const token = localStorage.getItem('token');
      const failed: string[] = [];

      for (const id of bulkHardDeleteConfirm.ids) {
        try {
          const resp = await fetch(`/api/admin/body-zones/${id}?hardDelete=true`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!resp.ok) failed.push(id);
        } catch {
          failed.push(id);
        }
      }

      setBulkHardDeleteConfirm({ isOpen: false, ids: [] });
      clearSelection();
      await fetchBodyZones();

      if (failed.length > 0) {
        setError(`${failed.length} zone(s) n’ont pas pu être supprimées définitivement.`);
      }
    } finally {
      setBulkHardDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getCategoryBadge = (category: string) => {
    const categoryConfig = {
      head: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Tête' },
      body: { bg: 'bg-green-100', text: 'text-green-800', label: 'Corps' },
      oral: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Oral' },
      genital: { bg: 'bg-rose-100', text: 'text-rose-800', label: 'Génital' },
    };
    
    const config = categoryConfig[category as keyof typeof categoryConfig] || { bg: 'bg-gray-100', text: 'text-gray-800', label: category };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  if (loading && bodyZones.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center space-x-2">
          <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          <span className="text-gray-600">Chargement des zones du corps...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="dt-list-toolbar flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center space-x-2">
              <span>Zones du Corps</span>
              <HelpIcon 
                docPath="10-donnees-reference#10-1-zones-corps" 
                tooltip="Documentation : Zones du corps"
              />
            </h1>
            <p className="text-textSecondary mt-1">
              {total} zone{total !== 1 ? 's' : ''} au total
            </p>
          </div>
          <button
            onClick={handleCreate}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Ajouter une Zone</span>
          </button>
          {selectedCount > 0 && (
            <div className="ml-3 flex items-center space-x-2">
              <button
                onClick={openBulkDeleteConfirm}
                disabled={bulkDeleting || bulkHardDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 inline-flex items-center space-x-2"
                title="Supprimer les zones sélectionnées"
              >
                <Trash2 className="w-4 h-4" />
                <span>Supprimer ({selectedCount})</span>
              </button>
              {canHardDelete && (
              <button
                onClick={openBulkHardDeleteConfirm}
                disabled={bulkDeleting || bulkHardDeleting}
                className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50 inline-flex items-center space-x-2"
                title="Supprimer définitivement les zones sélectionnées"
              >
                <AlertCircle className="w-4 h-4" />
                <span>Suppression définitive ({selectedCount})</span>
              </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="dt-filters grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Rechercher une zone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-field pl-10"
            />
          </div>

          {/* Category Filter */}
          <div className="relative">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="input-field appearance-none pr-10"
            >
              <option value="">Toutes les catégories</option>
              <option value="head">Tête</option>
              <option value="body">Corps</option>
              <option value="oral">Oral</option>
              <option value="genital">Génital</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>

          <div className="relative">
            <select
              value={collectionFilter}
              onChange={(e) => { setCollectionFilter(e.target.value); setPage(1); }}
              className="input-field appearance-none pr-10"
            >
              <option value="">Toutes les collections</option>
              {collections.map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({c.code})</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>

          {/* Clear Filters */}
          <button
            onClick={() => {
              setSearch('');
              setCategoryFilter('');
              setCollectionFilter('');
              setPage(1);
            }}
            className="btn-secondary flex items-center justify-center space-x-2"
          >
            <Filter className="w-4 h-4" />
            <span>Effacer</span>
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800 font-medium">{error}</span>
          </div>
          <button
            onClick={fetchBodyZones}
            className="mt-2 text-sm text-red-600 hover:text-red-700 underline"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Body Zones Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <TableScrollContainer hint="Faites défiler →">
          <ResponsiveTable className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                  <input
                    ref={selectAllCheckboxRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    disabled={bodyZones.length === 0 || bulkDeleting || bulkHardDeleting}
                    onChange={toggleSelectAllVisible}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    aria-label="Sélectionner toutes les zones de la page"
                  />
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center space-x-1">
                    <MapPin className="w-4 h-4" />
                    <span>Zone</span>
                    {sortKey === 'name' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('category')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Catégorie</span>
                    {sortKey === 'category' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Collection
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('createdAt')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Créé le</span>
                    {sortKey === 'createdAt' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 z-10 border-l border-gray-200">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {bodyZones.map((bodyZone) => (
                <tr key={bodyZone.id} className="group hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedBodyZoneIds.has(bodyZone.id)}
                      disabled={bulkDeleting || bulkHardDeleting}
                      onChange={() => toggleSelect(bodyZone.id)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      aria-label={`Sélectionner la zone ${bodyZone.name}`}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {bodyZone.name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getCategoryBadge(bodyZone.category)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {bodyZone.itemCollectionName || '-'}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 max-w-xs truncate">
                      {bodyZone.description || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(bodyZone.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium sticky right-0 bg-white group-hover:bg-gray-50 z-10 border-l border-gray-200">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleEdit(bodyZone)}
                        className="text-primary-600 hover:text-primary-700 p-1 rounded"
                        title="Modifier la zone"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(bodyZone.id)}
                        className="text-red-600 hover:text-red-700 p-1 rounded"
                        title="Supprimer la zone"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </ResponsiveTable>
        </TableScrollContainer>

        {/* Empty State */}
        {!loading && bodyZones.length === 0 && (
          <div className="text-center py-12">
            <MapPin className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune zone trouvée</h3>
            <p className="mt-1 text-sm text-gray-500">
              {search || categoryFilter 
                ? 'Essayez de modifier vos filtres de recherche.'
                : 'Commencez par ajouter votre première zone du corps.'
              }
            </p>
            <div className="mt-6">
              <button
                onClick={handleCreate}
                className="btn-primary flex items-center space-x-2 mx-auto"
              >
                <Plus className="w-4 h-4" />
                <span>Ajouter une Zone</span>
              </button>
            </div>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="dt-list-toolbar bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Précédent
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Affichage de <span className="font-medium">{(page - 1) * limit + 1}</span> à{' '}
                  <span className="font-medium">{Math.min(page * limit, total)}</span> sur{' '}
                  <span className="font-medium">{total}</span> résultats
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === pageNum
                            ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <BodyZoneModal
          bodyZone={editingBodyZone}
          collections={collections}
          onClose={() => {
            setShowCreateModal(false);
            setEditingBodyZone(null);
          }}
          onSuccess={() => {
            setShowCreateModal(false);
            setEditingBodyZone(null);
            fetchBodyZones();
          }}
        />
      )}

      {/* Bulk Delete Confirmation Dialog */}
      {bulkDeleteConfirm.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirmer la suppression</h3>
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer <strong>{bulkDeleteConfirm.ids.length}</strong> zone{bulkDeleteConfirm.ids.length !== 1 ? 's' : ''} ?
              Cette action ne peut pas être annulée.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelBulkDelete}
                disabled={bulkDeleting}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={confirmBulkDelete}
                disabled={bulkDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {bulkDeleting ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDefinitiveDeleteModal
        isOpen={bulkHardDeleteConfirm.isOpen}
        onConfirm={confirmBulkHardDelete}
        onClose={cancelBulkHardDelete}
        entityLabel="zone(s) du corps"
        count={bulkHardDeleteConfirm.ids.length}
        isLoading={bulkHardDeleting}
      />
    </div>
  );
}

interface BodyZoneModalProps {
  bodyZone: BodyZone | null;
  collections: ItemCollection[];
  onClose: () => void;
  onSuccess: () => void;
}

function BodyZoneModal({ bodyZone, collections, onClose, onSuccess }: BodyZoneModalProps) {
  const [formData, setFormData] = useState({
    name: bodyZone?.name || '',
    category: bodyZone?.category || 'head',
    description: bodyZone?.description || '',
    itemCollectionId: bodyZone?.itemCollectionId || collections[0]?.id || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const url = bodyZone 
        ? `/api/admin/body-zones/${bodyZone.id}`
        : '/api/admin/body-zones';
      
      const response = await fetch(url, {
        method: bodyZone ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to save body zone');
      }

      const data = await response.json();
      
      if (data.success) {
        onSuccess();
      } else {
        setError(data.message || 'Failed to save body zone');
      }
    } catch (error) {
      console.error('Save body zone error:', error);
      setError('Failed to save body zone');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          <h2 className="text-xl font-semibold text-textPrimary mb-4">
            {bodyZone ? 'Modifier la Zone' : 'Ajouter une Zone'}
          </h2>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Nom de la zone *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="input-field"
                placeholder="Oreille"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Catégorie *
              </label>
              <select
                required
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                className="input-field"
              >
                <option value="head">Tête</option>
                <option value="body">Corps</option>
                <option value="oral">Oral</option>
                <option value="genital">Génital</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Collection *
              </label>
              <select
                required
                value={formData.itemCollectionId}
                onChange={(e) => setFormData(prev => ({ ...prev, itemCollectionId: e.target.value }))}
                className="input-field"
              >
                <option value="">Choisir une collection</option>
                {collections.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} ({c.code})</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="input-field"
                placeholder="Description de la zone du corps..."
              />
            </div>

            <div className="flex items-center justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
                disabled={loading}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? 'Sauvegarde...' : (bodyZone ? 'Modifier' : 'Créer')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}